import { get, set } from '@ember/object';
import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { isBefore, addSeconds, subMinutes, isAfter, isDate } from 'date-fns';
import { storageFor } from 'ember-local-storage';
import fetch from 'fetch';
import config from 'invite-sign/config/environment';
import type EntryModel from 'invite-sign/models/entry';
import type FeatureFlagsService from 'invite-sign/services/feature-flags';
import { KIOSK_ENTRY_URL_KEY } from 'invite-sign/utils/kiosk-recovery';

type ProtectAuthData = {
  accessToken: string | null;
  refreshToken: string | null;
  expires: number | Date;
};

type ProtectFlowState = {
  [key: string]:
    | {
        purpose: Purpose;
        accessCode: string;
        meta: unknown;
      }
    | undefined;
};

export type VisitorEntry = {
  id: string;
  locationId: string;
  name: string | null;
  finalizedAt: Date | null;
  signedInAt: Date | null;
  createdAt: Date;
};

type StoredVisitorEntry = {
  [key: string]: VisitorEntry | undefined;
};

type SessionData = {
  access_token: string;
  expire_date: Date | number;
  refresh_token: string | null;
  token_type?: string;
};

type ErrorData = {
  message: string;
  company_logo?: string;
  company_name?: string;
  location_name?: string;
};

type SettingsData = {
  token?: string | null;
  setupAt?: Date;
  kioskId?: string;
  reset: () => void;
};

type Purpose = 'register' | 'sign-in' | 'sign-out';

export default class SessionService extends Service {
  @service declare featureFlags: FeatureFlagsService;

  @tracked sessionData: SessionData | null = null;
  @tracked errorData: ErrorData | null = null;
  @tracked timezone: string | undefined;

  @storageFor('protect-auth-data')
  declare protectAuthData: ProtectAuthData;

  @storageFor('protect-flow-state')
  declare protectFlowState: ProtectFlowState;

  @storageFor('visitor-entry')
  declare storedVisitorEntry: StoredVisitorEntry;

  @storageFor('settings')
  declare settings: SettingsData;

  get isAuthenticated(): boolean {
    if (!this.sessionData || isEmpty(this.sessionData)) {
      return false;
    }
    const expire_date = isDate(this.sessionData['expire_date'])
      ? this.sessionData['expire_date']
      : new Date(this.sessionData['expire_date']);
    return isBefore(new Date(), expire_date);
  }

  async getToken(body: unknown, clientId: string): Promise<void> {
    const sessionData = await this.#exchangeToken(body, clientId);
    if (sessionData) {
      this.sessionData = sessionData;
    }
  }

  async #exchangeToken(body: unknown, clientId: string): Promise<SessionData | null> {
    const base64ClientId = window.btoa(clientId.concat(':'));
    const options: RequestInit = {
      body: JSON.stringify(body),
      headers: {
        authorization: `Basic ${base64ClientId}`,
        'Content-Type': 'application/vnd.api+json',
      },
      method: 'POST',
      credentials: 'include',
    };
    const response = await fetch(`${config.apiHost}/a/auth/v0/token`, options);
    if (response.ok) {
      const sessionData = <SessionData>await response.json();
      // expires_in is an OAuth field expressed in seconds
      sessionData['expire_date'] = addSeconds(
        new Date(),
        (<SessionData & { expires_in: number }>sessionData)['expires_in'],
      );
      return sessionData;
    }
    const errorData = <{ error: ErrorData }>await response.json();

    if (errorData?.error) this.errorData = errorData.error;
    return null;
  }

  #lastGrant: unknown = null;
  #lastGrantClientId: string | null = null;
  #authGeneration = 0;
  #reauthenticatePromise: Promise<boolean> | null = null;

  authenticate(grant: unknown, clientId: string): Promise<void> {
    // Invalidate any in-flight replay so it can't overwrite this newer session.
    this.#authGeneration++;
    this.#reauthenticatePromise = null;
    this.#lastGrant = grant;
    this.#lastGrantClientId = clientId;
    return this.getToken(grant, clientId);
  }

  /**
   * Mints a fresh access token by replaying the grant most recently passed to
   * `authenticate`. Guest grants (guest codes, public device tokens) have no
   * refresh token, but their grant material stays valid and can be exchanged
   * again — which recovers a session whose access token expired mid-flow.
   * Concurrent callers share a single in-flight replay.
   */
  reauthenticate(): Promise<boolean> {
    if (!this.#reauthenticatePromise) {
      const promise = this.#replayLastGrant().finally(() => {
        if (this.#reauthenticatePromise === promise) {
          this.#reauthenticatePromise = null;
        }
      });
      this.#reauthenticatePromise = promise;
    }
    return this.#reauthenticatePromise;
  }

  async #replayLastGrant(): Promise<boolean> {
    if (this.#lastGrant == null || this.#lastGrantClientId == null) {
      return false;
    }
    const generation = this.#authGeneration;
    let sessionData: SessionData | null;
    try {
      sessionData = await this.#exchangeToken(this.#lastGrant, this.#lastGrantClientId);
    } catch (error) {
      console.error('Session reauthentication failed:', error); // eslint-disable-line no-console
      return false;
    }
    // A rejected exchange, or one superseded by a newer authenticate() while
    // in flight, must not replace the current session.
    if (sessionData == null || this.#authGeneration !== generation) {
      return false;
    }
    this.sessionData = sessionData;
    return this.isAuthenticated;
  }

  /**
   * Exchanges an auth code for an access token,
   * then saves it in local storage.
   */
  async authenticateWithCode(code: string, companyId: string | number): Promise<void> {
    await this.getToken({ grant_type: 'authorization_code', company_id: companyId, code }, config.guestClientId);
    set(this.protectAuthData, 'accessToken', (<SessionData>this.sessionData).access_token);
    set(this.protectAuthData, 'refreshToken', (<SessionData>this.sessionData).refresh_token);
    set(this.protectAuthData, 'expires', (<SessionData>this.sessionData).expire_date);
  }

  /**
   * Loads auth data from local storage,
   * and uses it if available and not expired.
   */
  useExistingAuthIfPossible(): boolean {
    if (!isEmpty(this.sessionData)) {
      return true;
    }
    const accessToken = get(this.protectAuthData, 'accessToken');
    const refreshToken = get(this.protectAuthData, 'refreshToken');
    const expires = isDate(get(this.protectAuthData, 'expires'))
      ? get(this.protectAuthData, 'expires')
      : new Date(get(this.protectAuthData, 'expires'));
    if (!accessToken || isAfter(new Date(), expires)) {
      this.sessionData = null;
      return false;
    }
    this.sessionData = {
      access_token: accessToken,
      refresh_token: refreshToken,
      expire_date: expires,
    };
    return true;
  }

  /**
   * Initiates a Protect flow by sending a magic link and saving the flow data in local storage.
   */
  async beginProtectFlow(
    name: string,
    email: string,
    purpose: Purpose,
    accessCode: string,
    stateId: string,
    meta: string | undefined = undefined,
  ): Promise<boolean> {
    set(this.protectFlowState, stateId, { purpose, accessCode, meta });
    const base64ClientId = window.btoa(config.guestClientId.concat(':'));
    const optional: { invite_id?: string | undefined; entry_id?: string | undefined } = {};
    switch (purpose) {
      case 'sign-in':
        optional.invite_id = meta;
        break;
      case 'sign-out':
        optional.entry_id = meta;
        break;
      default:
        break;
    }
    const options: RequestInit = {
      body: JSON.stringify({
        client_id: config.guestClientId,
        response_type: 'magic_link',
        transport: 'email',
        redirect_uri: `${config.invitesHost}/protect/${accessCode}/callback`,
        protect_access_code: accessCode,
        purpose: `protect-web-${purpose}`,
        state: stateId,
        email,
        name,
        ...optional,
      }),
      headers: {
        authorization: `Basic ${base64ClientId}`,
        'Content-Type': 'application/vnd.api+json',
      },
      method: 'POST',
      credentials: 'include',
    };
    const response = await fetch(`${config.apiHost}/a/auth/v0/authorize/passwordless`, options);
    return response.ok;
  }

  /**
   * Continues a Protect flow by retrieving the flow data from local storage.
   */
  continueProtectFlow(stateId: string): ProtectFlowState[string] | undefined {
    return get(this.protectFlowState, stateId);
  }

  /**
   * Ends a Protect flow by removing the flow data from local storage.
   */
  endProtectFlow(stateId: string): void {
    set(this.protectFlowState, stateId, undefined);
  }

  get stalenessBoundary(): Date {
    const isTtlOneWeek = this.featureFlags.isEnabled('signedInEntriesLookBack');

    // 7 days or 1 day in minutes (using minutes avoids Daylight Saving quirks)
    const minutesToBoundary = isTtlOneWeek ? 7 * 1440 : 1440;

    return subMinutes(new Date(), minutesToBoundary);
  }

  get storedEntries(): VisitorEntry[] {
    const storedEntries = Object.values(get(this, 'storedVisitorEntry.content') ?? {});

    return <VisitorEntry[]>storedEntries.filter(Boolean);
  }

  get isSharedDevice(): boolean {
    return Boolean(this.sharedDeviceToken);
  }

  get sharedDeviceToken(): string | null | undefined {
    return get(this.settings, 'token');
  }

  shareDeviceWithToken(token: string): void {
    set(this.settings, 'setupAt', new Date());
    set(this.settings, 'token', token);
    // Persist the entry URL so the global error handler can recover the kiosk
    // to the welcome screen on an uncaught error (SQ-7544). Best-effort.
    try {
      sessionStorage.setItem(KIOSK_ENTRY_URL_KEY, `/in/${token}/shared`);
    } catch {
      // sessionStorage may be unavailable
    }
  }

  ensureKioskId(): string {
    let kioskId = get(this.settings, 'kioskId');

    if (!kioskId) {
      kioskId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      set(this.settings, 'kioskId', kioskId);
    }

    return kioskId;
  }

  clearDeviceSettings(): void {
    get(this, 'settings').reset();
    try {
      sessionStorage.removeItem(KIOSK_ENTRY_URL_KEY);
    } catch {
      // sessionStorage may be unavailable
    }
  }

  isStoredEntryForLocation(locationId: string): boolean {
    return this.storedEntries.some((entry) => entry.locationId === locationId);
  }

  storedEntriesForLocation(locationId: string): VisitorEntry[] {
    return this.storedEntries.filter((entry) => entry.locationId === locationId);
  }

  removeStoredEntry(id: string): void {
    const { storedVisitorEntry } = this;
    const key = `entry-${id}`;

    set(storedVisitorEntry, key, undefined);
  }

  finalizeEntry(entry: EntryModel): void {
    const { fullName = '', id, finalizedAt, signedInAt, createdAt } = entry;
    const key = `entry-${id}`;
    const name = fullName.trim();
    const locationId = `${<string>get(entry, 'location.id') || ''}`;

    set(this.storedVisitorEntry, key, {
      id,
      locationId,
      name,
      finalizedAt,
      signedInAt,
      createdAt,
    });
  }

  cleanupStaleStoredVisitorEntries(): void {
    const { storedVisitorEntry, stalenessBoundary } = this;
    const keys = Object.keys(get(storedVisitorEntry, 'content') ?? {});

    keys.forEach((key: string): void => {
      const storedEntry = get(storedVisitorEntry, key);

      if (!storedEntry?.finalizedAt || isBefore(new Date(storedEntry.finalizedAt), stalenessBoundary)) {
        set(storedVisitorEntry, key, undefined);
      }
    });
  }
}
